/**
 * Suggested-question generator for the "Ask anything about this home" panel.
 *
 * Two axes of adaptation:
 *   1. MARKET-AWARE — wording swaps for IE / UK / US so a UK visitor sees
 *      "EPC" / "postcode" / "permitted development" / "SDLT" instead of
 *      "BER" / "Eircode" / "granny flat" / "stamp duty".
 *   2. PROPERTY-AWARE — chips get filtered/added based on the listing.
 *      Flats never see "add a granny flat"; garden-having listings gain
 *      an extension chip; period properties gain a condition chip.
 *
 * The output is a curated ~10-item list of natural-language questions.
 * Order matters — the top items are the "no-brainer" first-turn asks.
 *
 * Usage:
 *   const chips = buildSuggestions(property);
 *   // → ["What's the heating bill…", "Any planning apps nearby?", …]
 *
 * The upstream `ListingAskPanel` filters out chips the user has already
 * asked so the rail keeps evolving across a multi-turn session.
 */

// -------------------------------------------------------------- helpers
const _UK_COUNTRIES = /^(UK|GB|United Kingdom|Great Britain|England|Scotland|Wales|Northern Ireland)$/i;
const _US_COUNTRIES = /^(US|USA|United States)$/i;

const _detectMarket = (country) => {
  if (!country) return 'IE';
  if (_UK_COUNTRIES.test(country)) return 'UK';
  if (_US_COUNTRIES.test(country)) return 'US';
  return 'IE';
};

const _isFlat = (propertyType) => {
  const t = (propertyType || '').toLowerCase();
  return t.includes('apartment') || t.includes('flat') || t.includes('studio');
};

const _hasFeature = (features, needles) => {
  const bag = (features || []).map((f) => (f || '').toLowerCase()).join(' | ');
  return needles.some((n) => bag.includes(n));
};

const _isPeriod = (propertyType, features) => {
  const t = (propertyType || '').toLowerCase();
  if (t.includes('period') || t.includes('victorian') || t.includes('georgian') || t.includes('edwardian')) return true;
  return _hasFeature(features, ['period', 'victorian', 'georgian', 'edwardian', 'redbrick']);
};

// -------------------------------------------------------------- market copy
// Each entry is a template — the market-specific variant is picked once
// per property so the resulting chip text feels native to the visitor.
const MARKET_COPY = {
  IE: {
    energyLabel: 'BER',
    postcodeLabel: 'Eircode',
    heatingSwapChip: 'What\u2019s the heating bill if I switch to a heat pump?',
    firstTimeBuyerChip: 'Am I eligible for Help-to-Buy on this property?',
    stampDutyChip: 'How much stamp duty do I pay on this property?',
    schoolChip: 'What\u2019s the nearest primary school?',
    grannyFlatChip: 'Can I add a granny flat to this house?',
    permittedDevChip: 'What can I build without full planning permission?',
    floodChip: 'Is this in a flood-risk area?',
    planningChip: 'Any planning applications nearby I should know about?',
    stationChip: 'How close is the nearest train station?',
    walkabilityChip: 'How walkable is this street?',
    commuteChip: 'What\u2019s the typical commute time to the city centre?',
    diningChip: 'What are the good local restaurants and pubs?',
    cashAtCloseChip: 'How much cash do I really need at close?',
    extensionChip: 'Can I extend into the garden — what usually needs planning?',
    conditionChip: 'What condition should I expect from a period home like this?',
    freeholdChip: 'Is this freehold or leasehold, and does it matter here?',
    lptChip: 'What\u2019s the LPT (Local Property Tax) on this house?',
  },
  UK: {
    energyLabel: 'EPC',
    postcodeLabel: 'postcode',
    heatingSwapChip: 'What\u2019s the heating bill if I switch to a heat pump?',
    firstTimeBuyerChip: 'Am I eligible for the First Homes Scheme on this property?',
    stampDutyChip: 'How much SDLT (stamp duty) do I pay on this property?',
    schoolChip: 'What are the nearest Ofsted-rated primary schools?',
    grannyFlatChip: 'Can I add an annexe or granny flat under permitted development?',
    permittedDevChip: 'What can I build under permitted development here?',
    floodChip: 'Is this in an Environment Agency flood-risk zone?',
    planningChip: 'Any planning applications nearby I should know about?',
    stationChip: 'How close is the nearest rail or tube station?',
    walkabilityChip: 'How walkable is this street?',
    commuteChip: 'What\u2019s the typical commute time to the city centre?',
    diningChip: 'What are the good local restaurants and pubs?',
    cashAtCloseChip: 'How much cash do I really need to exchange and complete?',
    extensionChip: 'Can I extend into the garden — what usually needs planning?',
    conditionChip: 'What condition should I expect from a period home like this?',
    freeholdChip: 'Is this freehold or leasehold, and how long is left on any lease?',
    lptChip: 'What\u2019s the Council Tax band on this property?',
  },
  US: {
    energyLabel: 'Energy Score',
    postcodeLabel: 'ZIP',
    heatingSwapChip: 'What\u2019s the heating and cooling bill if I switch to a heat pump?',
    firstTimeBuyerChip: 'Am I eligible for any first-time-buyer programmes here?',
    stampDutyChip: 'What are the typical closing costs on this property?',
    schoolChip: 'What are the nearest highly-rated public schools?',
    grannyFlatChip: 'Can I add an ADU (accessory dwelling unit) on this lot?',
    permittedDevChip: 'What can I build without a full zoning variance?',
    floodChip: 'Is this in a FEMA flood zone?',
    planningChip: 'Any zoning or permit applications nearby I should know about?',
    stationChip: 'How close is the nearest transit or commuter rail station?',
    walkabilityChip: 'How walkable is this street?',
    commuteChip: 'What\u2019s the typical commute time to downtown?',
    diningChip: 'What are the well-known local restaurants and cafés?',
    cashAtCloseChip: 'How much cash do I really need at close (down + closing)?',
    extensionChip: 'Can I extend into the yard — what usually needs a permit?',
    conditionChip: 'What condition should I expect from an older home like this?',
    freeholdChip: 'Are there any HOA fees or covenants on this property?',
    lptChip: 'What are the annual property taxes on this home?',
  },
};

// -------------------------------------------------------------- public API
/**
 * Return an ordered list of suggested questions for a given property.
 * Never returns duplicates, never returns nonsensical chips (no "granny
 * flat" on a flat). Length is capped at 12 so the frontend can slice
 * freely without worrying about ballooning.
 */
export const buildSuggestions = (property) => {
  const country = property?.location?.country || property?.country || '';
  const market = _detectMarket(country);
  const M = MARKET_COPY[market] || MARKET_COPY.IE;
  const type = property?.propertyType || property?.type || '';
  const features = property?.features || [];
  const flat = _isFlat(type);
  const hasGarden = _hasFeature(features, ['garden', 'yard', 'grounds']);
  const period = _isPeriod(type, features);

  const chips = [];

  // 1. High-signal defaults — always relevant
  chips.push(M.heatingSwapChip);
  chips.push(M.floodChip);
  chips.push(M.cashAtCloseChip);
  chips.push(M.schoolChip);
  chips.push(M.stationChip);
  chips.push(M.commuteChip);
  chips.push(M.planningChip);

  // 2. Property-shape-specific
  if (!flat) {
    // Houses: extension / annexe potential is useful
    chips.push(M.grannyFlatChip);
    chips.push(M.permittedDevChip);
  } else {
    // Flats: freehold/leasehold is the top-of-mind question
    chips.push(M.freeholdChip);
  }
  if (hasGarden && !flat) {
    chips.push(M.extensionChip);
  }

  // 3. Feature-specific
  if (period) {
    chips.push(M.conditionChip);
  }

  // 4. Market-fixed tax + FTB helpers
  chips.push(M.stampDutyChip);
  chips.push(M.firstTimeBuyerChip);
  chips.push(M.lptChip);
  chips.push(M.walkabilityChip);
  chips.push(M.diningChip);

  // Dedupe (defensively — market templates should already be unique) and cap.
  const seen = new Set();
  const out = [];
  for (const c of chips) {
    const key = c.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
    if (out.length >= 12) break;
  }
  return out;
};

export default buildSuggestions;
